import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import {
  Award,
  Users,
  ShieldCheck,
  Calendar,
  MapPin,
  ArrowRight,
  Loader2,
  CheckCircle2,
  Mail,
} from "lucide-react";
import { toast } from "sonner";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SHOWCASE_OPTIONS = [
  "Men's Showcase (March 19–21, 2027)",
  "Women's Showcase (April 9–11, 2027)",
  "Both Showcases",
];

const CERTIFICATIONS = [
  "TSOA Certified",
  "NCAA Certified",
  "TSOA + NCAA Certified",
  "Other State Certification",
  "Not Yet Certified",
];

const YEARS = ["Under 2", "2–5", "6–10", "11–20", "20+"];
const COACHING_LEVELS = [
  "Youth / AAU",
  "High School",
  "JUCO",
  "NCAA Div III",
  "NCAA Div II",
  "NCAA Div I",
  "Overseas / Pro",
  "Other",
];

const CoachOfficialRegisterPage = () => {
  const [form, setForm] = useState({
    role_type: "coach",
    showcase_preference: SHOWCASE_OPTIONS[2],
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    city_state: "",
    certification: "",
    years_experience: "",
    current_affiliation: "",
    coaching_level: "",
    resume_link: "",
    references: "",
    availability: "",
    notes: "",
    agree_to_terms: false,
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(null);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    for (const k of ["first_name", "last_name", "email", "phone"]) {
      if (!String(form[k] || "").trim()) {
        toast.error(`Please complete: ${k.replace("_", " ")}.`);
        return;
      }
    }
    if (!form.agree_to_terms) {
      toast.error("Please acknowledge the certification.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/coaches-officials`, form);
      toast.success(`Thanks ${data.first_name} — we'll be in touch.`);
      setSubmitted(data);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      const detail = err?.response?.data?.detail || "Submission failed.";
      toast.error(typeof detail === "string" ? detail : "Submission failed.");
    } finally {
      setLoading(false);
    }
  };

  const isCoach = form.role_type === "coach";
  const inputCls =
    "w-full bg-transparent border-b border-neutral-800 focus:border-amber-400 text-white py-3 outline-none placeholder:text-neutral-700 [color-scheme:dark]";
  const labelCls = "text-[10px] uppercase tracking-[0.3em] text-neutral-400";

  return (
    <div className="App bg-black text-white min-h-screen">
      <Navbar />

      {/* Hero */}
      <section className="pt-32 md:pt-40 pb-14 border-b border-neutral-900 bg-gradient-to-b from-[#0a0a0a] to-black">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12">
          <div className="flex items-center gap-3 text-[10px] uppercase tracking-[0.45em] text-amber-400 mb-6">
            <span className="w-8 h-px bg-amber-400" />
            <Award size={14} />
            <span>Coaches & Officials · Signup</span>
          </div>
          <motion.h1
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            data-testid="coaches-title"
            className="font-display uppercase text-5xl md:text-7xl lg:text-8xl leading-[0.88] text-white"
          >
            We need
            <br />
            <span className="text-amber-400">you.</span>
          </motion.h1>
          <p className="text-neutral-300 max-w-2xl mt-6 font-light leading-relaxed">
            Coaches, TSOA and NCAA-certified officials — join the team that&apos;s
            building the bridge between African and American basketball. Sign up
            below or send your resume to{" "}
            <a
              href="mailto:afroambasketball@gmail.com?subject=Coach%20%2F%20Official%20Signup"
              className="text-amber-300 hover:text-amber-400"
            >
              afroambasketball@gmail.com
            </a>
            .
          </p>
        </div>
      </section>

      {submitted ? (
        <section className="py-16 md:py-24 bg-black border-b border-neutral-900">
          <div className="max-w-[900px] mx-auto px-6 md:px-12">
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="relative border border-amber-400/40 bg-[#0a0a0a] p-8 md:p-12"
              data-testid="coaches-success"
            >
              <CheckCircle2 size={42} className="text-amber-400 mb-6" />
              <h2 className="font-display uppercase text-4xl md:text-6xl text-white leading-[0.95]">
                Signup
                <br />
                <span className="text-amber-400">received.</span>
              </h2>
              <p className="text-neutral-300 mt-6 leading-relaxed">
                Thanks <span className="text-white">{submitted.first_name}</span> — your{" "}
                <span className="text-white">{submitted.role_type}</span> signup for{" "}
                <span className="text-white">{submitted.showcase_preference}</span> is
                in. We&apos;ll reach out at{" "}
                <span className="text-white">{submitted.email}</span> with next
                steps.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <a
                  href="/"
                  className="inline-flex items-center gap-2 border border-white text-white px-6 py-3.5 text-xs uppercase tracking-[0.28em] font-bold hover:bg-white hover:text-black transition-colors"
                >
                  Back Home
                </a>
                <a
                  href="mailto:afroambasketball@gmail.com?subject=Coach%20%2F%20Official%20Resume"
                  className="inline-flex items-center gap-2 bg-amber-400 text-black px-6 py-3.5 text-xs uppercase tracking-[0.28em] font-bold hover:bg-amber-300 transition-colors"
                >
                  <Mail size={14} /> Send Resume
                </a>
              </div>
            </motion.div>
          </div>
        </section>
      ) : (
        <section className="py-14 md:py-20 border-b border-neutral-900">
          <div className="max-w-[1440px] mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-10">
            {/* Left: flyer + info */}
            <div className="lg:col-span-5">
              <div className="text-[10px] uppercase tracking-[0.4em] text-neutral-500 mb-4">
                — Step 01 · Pick a role
              </div>

              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => setField("role_type", "coach")}
                  data-testid="role-coach"
                  className={`text-left p-5 border transition-all ${
                    isCoach
                      ? "border-amber-400 bg-[#0a0a0a]"
                      : "border-neutral-800 bg-black hover:border-neutral-600"
                  }`}
                >
                  <Users
                    size={18}
                    className={isCoach ? "text-amber-400" : "text-neutral-500"}
                  />
                  <div
                    className={`mt-3 text-[10px] uppercase tracking-[0.35em] ${
                      isCoach ? "text-amber-400" : "text-neutral-500"
                    }`}
                  >
                    {isCoach ? "Selected" : "Available"}
                  </div>
                  <div className="font-display uppercase text-xl md:text-2xl text-white mt-1">
                    Coach
                  </div>
                  <div className="text-[10px] uppercase tracking-[0.25em] text-neutral-500 mt-1">
                    Men & Women
                  </div>
                </button>
                <button
                  type="button"
                  onClick={() => setField("role_type", "official")}
                  data-testid="role-official"
                  className={`text-left p-5 border transition-all ${
                    !isCoach
                      ? "border-amber-400 bg-[#0a0a0a]"
                      : "border-neutral-800 bg-black hover:border-neutral-600"
                  }`}
                >
                  <ShieldCheck
                    size={18}
                    className={!isCoach ? "text-amber-400" : "text-neutral-500"}
                  />
                  <div
                    className={`mt-3 text-[10px] uppercase tracking-[0.35em] ${
                      !isCoach ? "text-amber-400" : "text-neutral-500"
                    }`}
                  >
                    {!isCoach ? "Selected" : "Available"}
                  </div>
                  <div className="font-display uppercase text-xl md:text-2xl text-white mt-1">
                    Official
                  </div>
                  <div className="text-[10px] uppercase tracking-[0.25em] text-neutral-500 mt-1">
                    TSOA / NCAA
                  </div>
                </button>
              </div>

              <div className="mt-8 border border-neutral-900 bg-[#070707] p-5 space-y-3">
                <div className="flex items-start gap-3">
                  <Calendar size={14} className="text-amber-400 mt-1 shrink-0" />
                  <div className="text-sm text-neutral-300">
                    <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-500">
                      Men&apos;s Showcase
                    </div>
                    March 19 – 21, 2027
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Calendar size={14} className="text-amber-400 mt-1 shrink-0" />
                  <div className="text-sm text-neutral-300">
                    <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-500">
                      Women&apos;s Showcase
                    </div>
                    April 9 – 11, 2027
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <MapPin size={14} className="text-amber-400 mt-1 shrink-0" />
                  <div className="text-sm text-neutral-300">
                    Prairie View A&amp;M · Texas A&amp;M College Station
                  </div>
                </div>
              </div>

              <div className="hidden lg:block mt-8">
                <img
                  src="/brand/coaches-officials-flyer.jpg"
                  alt="AfroAm Coaches & Officials Flyer"
                  className="w-full h-auto border border-neutral-900"
                  data-testid="coaches-flyer"
                />
              </div>
            </div>

            {/* Right: form */}
            <div className="lg:col-span-7">
              <div className="text-[10px] uppercase tracking-[0.4em] text-neutral-500 mb-4">
                — Step 02 · Your details
              </div>
              <form onSubmit={submit} className="space-y-8" data-testid="coaches-form">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className={labelCls}>First Name *</label>
                    <input
                      className={inputCls}
                      value={form.first_name}
                      onChange={(e) => setField("first_name", e.target.value)}
                      placeholder="Alex"
                      data-testid="co-first"
                      required
                    />
                  </div>
                  <div>
                    <label className={labelCls}>Last Name *</label>
                    <input
                      className={inputCls}
                      value={form.last_name}
                      onChange={(e) => setField("last_name", e.target.value)}
                      placeholder="Rivers"
                      data-testid="co-last"
                      required
                    />
                  </div>
                  <div>
                    <label className={labelCls}>Email *</label>
                    <input
                      type="email"
                      className={inputCls}
                      value={form.email}
                      onChange={(e) => setField("email", e.target.value)}
                      placeholder="you@example.com"
                      data-testid="co-email"
                      required
                    />
                  </div>
                  <div>
                    <label className={labelCls}>Phone *</label>
                    <input
                      className={inputCls}
                      value={form.phone}
                      onChange={(e) => setField("phone", e.target.value)}
                      placeholder="(555) 123-4567"
                      data-testid="co-phone"
                      required
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className={labelCls}>City / State</label>
                    <input
                      className={inputCls}
                      value={form.city_state}
                      onChange={(e) => setField("city_state", e.target.value)}
                      placeholder="Houston, TX"
                      data-testid="co-city"
                    />
                  </div>
                  <div>
                    <label className={labelCls}>Which Showcase *</label>
                    <select
                      className={`${inputCls} appearance-none`}
                      value={form.showcase_preference}
                      onChange={(e) => setField("showcase_preference", e.target.value)}
                      data-testid="co-showcase"
                      required
                    >
                      {SHOWCASE_OPTIONS.map((o) => (
                        <option key={o}>{o}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className={labelCls}>Years of Experience</label>
                    <select
                      className={`${inputCls} appearance-none`}
                      value={form.years_experience}
                      onChange={(e) => setField("years_experience", e.target.value)}
                      data-testid="co-years"
                    >
                      <option value="">Select range</option>
                      {YEARS.map((y) => (
                        <option key={y}>{y}</option>
                      ))}
                    </select>
                  </div>
                  {isCoach ? (
                    <div className="md:col-span-2">
                      <label className={labelCls}>Coaching Level</label>
                      <select
                        className={`${inputCls} appearance-none`}
                        value={form.coaching_level}
                        onChange={(e) => setField("coaching_level", e.target.value)}
                        data-testid="co-level"
                      >
                        <option value="">Select level</option>
                        {COACHING_LEVELS.map((c) => (
                          <option key={c}>{c}</option>
                        ))}
                      </select>
                    </div>
                  ) : (
                    <div className="md:col-span-2">
                      <label className={labelCls}>Certification *</label>
                      <select
                        className={`${inputCls} appearance-none`}
                        value={form.certification}
                        onChange={(e) => setField("certification", e.target.value)}
                        data-testid="co-cert"
                        required={!isCoach}
                      >
                        <option value="">Select certification</option>
                        {CERTIFICATIONS.map((c) => (
                          <option key={c}>{c}</option>
                        ))}
                      </select>
                    </div>
                  )}
                  <div className="md:col-span-2">
                    <label className={labelCls}>
                      Current Team / Program / Officials Assoc.
                    </label>
                    <input
                      className={inputCls}
                      value={form.current_affiliation}
                      onChange={(e) => setField("current_affiliation", e.target.value)}
                      placeholder="e.g., Houston Hawks · Texas Southern · TSOA Houston Chapter"
                      data-testid="co-affiliation"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className={labelCls}>Resume Link (optional)</label>
                    <input
                      className={inputCls}
                      value={form.resume_link}
                      onChange={(e) => setField("resume_link", e.target.value)}
                      placeholder="Drive / Dropbox / LinkedIn URL"
                      data-testid="co-resume"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className={labelCls}>References</label>
                    <textarea
                      className={`${inputCls} resize-none`}
                      value={form.references}
                      onChange={(e) => setField("references", e.target.value)}
                      placeholder="Name, role, email/phone — one per line"
                      rows={3}
                      data-testid="co-references"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className={labelCls}>Availability / Travel</label>
                    <textarea
                      className={`${inputCls} resize-none`}
                      value={form.availability}
                      onChange={(e) => setField("availability", e.target.value)}
                      placeholder="Which weekends can you work? Any travel needs?"
                      rows={2}
                      data-testid="co-availability"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className={labelCls}>Anything else we should know?</label>
                    <textarea
                      className={`${inputCls} resize-none`}
                      value={form.notes}
                      onChange={(e) => setField("notes", e.target.value)}
                      placeholder="Specialties, philosophy, past championships, etc."
                      rows={3}
                      data-testid="co-notes"
                    />
                  </div>
                </div>

                <label className="flex items-start gap-3 text-xs text-neutral-300 leading-relaxed cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.agree_to_terms}
                    onChange={(e) => setField("agree_to_terms", e.target.checked)}
                    className="mt-0.5 w-4 h-4 accent-amber-400"
                    data-testid="co-agree"
                  />
                  <span>
                    I confirm the information above is accurate and understand
                    that AfroAm Basketball Association may contact me to schedule
                    interviews, verify certifications, and finalize assignments.
                  </span>
                </label>

                <div className="pt-4 border-t border-neutral-900 flex flex-wrap items-center gap-4">
                  <button
                    type="submit"
                    disabled={loading}
                    data-testid="co-submit"
                    className="inline-flex items-center gap-3 bg-amber-400 text-black px-8 py-4 text-xs uppercase tracking-[0.28em] font-bold hover:bg-amber-300 transition-colors disabled:opacity-60"
                  >
                    {loading ? (
                      <Loader2 className="animate-spin" size={14} />
                    ) : (
                      <ArrowRight size={14} />
                    )}
                    Submit Signup
                  </button>
                  <span className="text-[10px] uppercase tracking-[0.28em] text-neutral-500">
                    Signing up as{" "}
                    <span className="text-amber-400">
                      {isCoach ? "Coach" : "Official"}
                    </span>
                  </span>
                </div>
              </form>
            </div>
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
};

export default CoachOfficialRegisterPage;
